<template>
	<div
		class="operators-table-container"
		:class="{ 'compact-mode': showOnlyOperators }"
	>
		<div
			class="table-actions"
			v-if="showOnlyOperators"
		>
			<b-button
				size="sm"
				variant="success"
				class="export-btn"
				@click="exportCsv"
			>
				<i class="fas fa-file-csv"></i>
				CSV
			</b-button>
		</div>

		<b-table
			id="operators-table"
			:class="{ 'compact-table': showOnlyOperators }"
			:items="operators"
			:fields="fields"
			:small="showOnlyOperators"
			hover
			responsive
			show-empty
			borderless
			empty-text="Nenhum operador encontrado"
			sticky-header
			:style="{ 'max-height': '100%' }"
		>
			<template #head(name)="data">
				<span class="table-head">
					<i class="fas fa-user"></i>
					{{ data.label }}
				</span>
			</template>

			<template #head(extension_line)="data">
				<span class="table-head">
					<i class="fas fa-phone-square"></i>
					{{ data.label }}
				</span>
			</template>

			<template #head(received)="data">
				<span class="table-head">
					<i class="fas fa-phone"></i>
					{{ data.label }}
				</span>
			</template>

			<template #head(answered)="data">
				<span class="table-head">
					<i class="fas fa-phone-volume"></i>
					{{ data.label }}
				</span>
			</template>

			<template #head(outgoing)="data">
				<span class="table-head">
					<i class="fas fa-phone-alt"></i>
					{{ data.label }}
				</span>
			</template>

			<template #head(notAnswered)="data">
				<span class="table-head">
					<i class="fas fa-phone-slash"></i>
					{{ data.label }}
				</span>
			</template>

			<template #head(attendanceTime)="data">
				<span class="table-head">
					<i class="fas fa-clock"></i>
					{{ data.label }}
				</span>
			</template>

			<template #cell(attendanceTime)="data">
				{{ parseTma(data.item.attendanceTime, data.item.answered, data.item.outgoing) }}
			</template>
		</b-table>
	</div>
</template>

<script>
import moment from 'moment';

export default {
	props: ["filter", "data", "showOnlyOperators"],

	data() {
		return {
			loading: false,

			startDate: moment().format('YYYY-MM-DD 00:00:00'),
			endDate: moment().format('YYYY-MM-DD 23:59:59'),

			fields: [
				{
					key: 'name',
					label: 'Operador',
					thClass: ['custom-column', 'name-header'],
					tdClass: ['td-name'],
					sortable: false
				},
				{
					key: 'extension_line',
					label: 'Ramal',
					thClass: ['custom-column', 'extension-header'],
					tdClass: ['td-center'],
					sortable: false
				},
				{
					key: 'received',
					label: 'Rec',
					thClass: ['custom-column', 'received-header'],
					tdClass: ['td-center'],
					sortable: false
				},
				{
					key: 'answered',
					label: 'Atd',
					thClass: ['custom-column', 'answered-header'],
					tdClass: ['td-center'],
					sortable: false
				},
				{
					key: 'outgoing',
					label: 'Sai',
					thClass: ['custom-column', 'outgoing-header'],
					tdClass: ['td-center'],
					sortable: false
				},
				{
					key: 'notAnswered',
					label: 'Per',
					thClass: ['custom-column', 'not-answered-header'],
					tdClass: ['td-center'],
					sortable: false
				},
				{
					key: 'attendanceTime',
					label: 'TMA',
					thClass: ['custom-column', 'time-header'],
					tdClass: ['td-center'],
					sortable: false
				}
			]
		};
	},

	computed: {
		operators() {
			return Object.values(this.data);
		}
	},

	methods: {
		formatDuration(seconds) {
			const duration = moment.duration(seconds, 'seconds');

			const hours = Math.floor(duration.asHours())
				.toString()
				.padStart(2, '0');

			const minutes = duration.minutes()
				.toString()
				.padStart(2, '0');

			const secs = duration.seconds()
				.toString()
				.padStart(2, '0');

			return `${hours}:${minutes}:${secs}`;
		},

		parseTma(seconds, answered, outgoing) {
			let tma = 0;

			if (answered > 0 || outgoing > 0) {
				tma = (
					seconds / (answered + outgoing)
				).toFixed(2);
			}

			return this.formatDuration(tma);
		},

		exportCsv() {
			const headers = [
				'Operador',
				'Ramal',
				'Recebidas',
				'Atendidas',
				'Realizadas',
				'Perdidas',
				'TMA'
			];

			const rows = this.operators.map(operator => [
				operator.name,
				operator.extension_line,
				operator.received,
				operator.answered,
				operator.outgoing,
				operator.notAnswered,
				this.parseTma(
					operator.attendanceTime,
					operator.answered,
					operator.outgoing
				)
			]);

			const csvContent = [
				headers.join(','),
				...rows.map(row =>
					row.map(value => `"${value}"`).join(',')
				)
			].join('\n');

			const blob = new Blob(
				[csvContent],
				{
					type: 'text/csv;charset=utf-8;'
				}
			);

			const link = document.createElement('a');

			const url = URL.createObjectURL(blob);

			link.setAttribute('href', url);

			link.setAttribute(
				'download',
				`operadores-${moment().format('YYYY-MM-DD_HH-mm')}.csv`
			);

			link.style.visibility = 'hidden';

			document.body.appendChild(link);

			link.click();

			document.body.removeChild(link);
		}
	}
};
</script>

<style lang="scss">
.operators-table-container {
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.table-actions {
	display: flex;
	justify-content: flex-end;
	margin-bottom: 4px;
	flex-shrink: 0;
}

.export-btn {
	padding: 2px 8px !important;
	font-size: 11px !important;

	i {
		margin-right: 4px;
	}
}

#operators-table {
	flex: 1;
	min-height: 0;

	.custom-column {
		text-align: center;
		white-space: nowrap;
	}

	.table-head {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 4px;
		white-space: nowrap;
	}

	.td-name {
		font-weight: 600;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.td-center {
		text-align: center;
	}

	.name-header {
		background-color: #323031 !important;
		color: #fff !important;
	}

	.extension-header {
		background-color: #6c757d !important;
		color: #fff !important;
	}

	.received-header {
		background-color: #023e8a !important;
		color: #fff !important;
	}

	.answered-header {
		background-color: #0ead69 !important;
		color: #fff !important;
	}

	.outgoing-header {
		background-color: #7b2cbf !important;
		color: #fff !important;
	}

	.not-answered-header {
		background-color: #ef233c !important;
		color: #fff !important;
	}

	.time-header {
		background-color: #ffb600 !important;
		color: #fff !important;
	}

	.table-responsive {
		margin: 0 !important;
	}

	.b-table-sticky-header {
		margin: 0 !important;
		max-height: 100% !important;
	}
}

/* ========================= */
/* COMPACT MODE ONLY */
/* ========================= */

.compact-mode {
	.table-actions {
		margin-bottom: 2px;
	}

	.export-btn {
		padding: 1px 6px !important;
		font-size: 9px !important;

		i {
			margin-right: 2px;
			font-size: 9px;
		}
	}

	#operators-table.compact-table {
		font-size: 9px !important;
		line-height: 0.9;
		table-layout: fixed;

		th,
		td {
			padding: 2px 4px !important;
			vertical-align: middle !important;
			border: 0 !important;
		}

		thead th {
			font-size: 9px !important;
			font-weight: 600;
			height: 20px;
		}

		tbody td {
			font-size: 9px !important;
			height: 18px;
			font-weight: 500;
		}

		.table-head {
			gap: 2px;

			i {
				font-size: 8px;
			}
		}

		.td-name {
			max-width: 90px;
		}

		.name-header {
			border-radius: 4px 0 0 0;
		}

		.time-header {
			border-radius: 0 4px 0 0;
		}

		.table-responsive {
			margin: 0 !important;
		}

		.b-table-sticky-header {
			margin: 0 !important;
			max-height: 100% !important;
		}
	}
}
</style>